import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AccessService, hasAccess, hasGroupAccess } from '@socialgoodsoftware/frbac';
import { createUserFeaturesApi } from './user-features-api';
import { emptyFeatures, readFeaturesFromDocument } from './feature-seed';

import type { ReactNode } from 'react';
import type { ResolvedFeatures } from '@socialgoodsoftware/frbac';
import type { EffectiveFeatures } from './user-features-api';

export type FeatureContextValue = {
  /** The four sets FRBAC resolves, so every caller asks FRBAC's own question rather than a copy. */
  features: ResolvedFeatures;
  hasAccess: (featureGuid: string | string[]) => boolean;
  hasGroupAccess: (groupGuid: string) => boolean;
  /** The one rule every navigation in the suite asks, baseline included. */
  isMenuGroupVisible: (groupGuid: string) => boolean;
  isLoading: boolean;
  /** Re-reads the answer, for a host that signs a member in without reloading the page. */
  refresh: () => Promise<void>;
};

const FeatureContext = createContext<FeatureContextValue | undefined>(undefined);

function toResolvedFeatures(features: EffectiveFeatures): ResolvedFeatures {
  return {
    allow: new Set(features.allow),
    deny: new Set(features.deny),
    overrides: { allow: new Set(features.overrides.allow), deny: new Set(features.overrides.deny) },
  };
}

export type FeatureProviderProps = {
  /**
   * Who the answer belongs to — the member's email once the host knows it. A host that signs a
   * member in without reloading the page mounts this provider as a guest, so the answer is
   * re-read whenever this changes rather than staying at whatever was true at mount.
   */
  sessionKey?: string;
  children: ReactNode;
};

/**
 * The features the signed-in member holds, for every app's navigation and in-page affordances.
 * The server stamps them into the page, so the first frame is already correct; the fetch behind
 * it is the fallback for a host that does not seed and the refresh after a role change.
 *
 * Advisory only — the route policy on the server is what actually denies. What this decides is
 * whether a member is offered a door, not whether it opens.
 */
export function FeatureProvider({ sessionKey = '', children }: FeatureProviderProps) {
  const [seeded] = useState(() => readFeaturesFromDocument());
  const [features, setFeatures] = useState<EffectiveFeatures | null>(seeded);
  const apiRef = useRef(createUserFeaturesApi());
  const requested = useRef<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setFeatures(await apiRef.current.getFeatures());
    } catch {} // Keep the answer we have; the next navigation retries
  }, []);

  // Guarded because StrictMode invokes a mount effect twice, and this one is a request.
  useEffect(() => {
    if (sessionKey !== '' && requested.current !== sessionKey) {
      requested.current = sessionKey;
      void refresh();
    }
  }, [refresh, sessionKey]);

  const resolved = useMemo<ResolvedFeatures>(() => {
    return toResolvedFeatures(features ?? emptyFeatures());
  }, [features]);

  const isLoading = features === null;

  const checkAccess = useCallback(
    (featureGuid: string | string[]) => {
      let allowed = false;
      if (!isLoading) {
        allowed = hasAccess(featureGuid, resolved);
      }
      return allowed;
    },
    [resolved, isLoading]
  );

  const checkGroupAccess = useCallback(
    (groupGuid: string) => {
      let allowed = false;
      if (!isLoading) {
        allowed = hasGroupAccess(groupGuid, resolved);
      }
      return allowed;
    },
    [resolved, isLoading]
  );

  const checkMenuGroup = useCallback(
    (groupGuid: string) => {
      let visible = false;
      if (!isLoading) {
        visible = AccessService.IsMenuGroupVisible(groupGuid, resolved);
      }
      return visible;
    },
    [resolved, isLoading]
  );

  const value = useMemo<FeatureContextValue>(() => {
    return {
      features: resolved,
      hasAccess: checkAccess,
      hasGroupAccess: checkGroupAccess,
      isMenuGroupVisible: checkMenuGroup,
      isLoading: isLoading,
      refresh: refresh,
    };
  }, [resolved, checkAccess, checkGroupAccess, checkMenuGroup, isLoading, refresh]);

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
}

export function useFeatures(): FeatureContextValue {
  const context = useContext(FeatureContext);
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
}
